import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import * as micropython from './micropython';
import * as tios from './tios';
import * as zephyr from './zephyr';
import * as simulator from './simulator';


export default class TimerStartNode extends BaseNode {
    constructor() {
        super();
        this.name = 'Timer Start';
        this.icon = 'IoIosTimer';
        this.id = 'node_timer_start';
        this.linkedFeature = 'general';

        this.inputs = [
            {
                name: 'Start',
                portId: 'timer_start',
                params: [
                    {
                        name: 'time',
                        description: 'Time in seconds',
                        dataType: TibboDataType.WORD,
                        default: '1',
                    },
                ],
            },
        ];

        const settings: Setting[] = [
            {
                id: 'TIMER',
                name: 'Timer',
                settingType: SettingType.OPTION,
                refId: 'timer',
                required: true,
                default: '',
                options: [],
            },
            {
                id: 'REPEAT',
                name: 'Repeat',
                settingType: SettingType.OPTION,
                required: true,
                default: 'false',
                options: [
                    {
                        name: 'No',
                        value: 'false',
                    },
                    {
                        name: 'Yes',
                        value: 'true',
                    },
                ],
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getSettingValue(settingId: string): string {
        if (settingId === 'DISPLAY_NAME') {
            const project: ProjectConfiguration = this.project;
            const value = super.getSettingValue(settingId);
            if (value !== '') {
                return value;
            }
            const TIMER = project.getFeature(this.getSettingValue('TIMER'));
            if (TIMER) {
                const links = project.getNodeLinks(this, 'timer_start');
                let time = '';
                if (links.length > 0) {
                    time = project.getNodeParameter(links[0], 'time');
                }
                return `TIMER {{$${TIMER.getSettingValue('TIMER_NAME')}}} set to ${time} seconds`;
            }
        }
        return super.getSettingValue(settingId);
    }

    validate(): ValidationMessage[] {
        const messages: ValidationMessage[] = [];
        const TIMER = this.project.getFeature(this.getSettingValue('TIMER'));
        if (TIMER) {
            // find the timer completed node
            let found = false;
            for (let i = 0; i < this.project.features.length; i++) {
                const feature = this.project.features[i];
                if (feature.id === 'node_timer_completed') {
                    // see if block is referencing in the same timer
                    const completedNodeTimer = feature.getSettingValue('TIMER');
                    if (completedNodeTimer === this.getSettingValue('TIMER')) {
                        found = true;
                        break;
                    }
                }
            }
            if (!found) {
                messages.push({
                    messageType: ValidationMessageType.ERROR,
                    message: 'On Timer Completed block not found',
                    featureId: this.getID(),
                    settingId: 'TIMER',
                });
            }
        }
        return messages;
    }

    getName(): string {
        const project: ProjectConfiguration = this.project;
        const TIMER = project.getFeature(this.getSettingValue('TIMER'));
        if (TIMER) {
            const links = project.getNodeLinks(this, 'timer_start');
            let time = '';
            if (links.length > 0) {
                time = project.getNodeParameter(links[0], 'time');
            }
            return `TIMER {{$${TIMER.getSettingValue('TIMER_NAME')}}} set to ${time} seconds`;
        }
        return this.name;
    }

    getCodeBlocks(): CodeBlock[] {
        switch (this.project.runtime) {
            case 'micropython':
                return micropython.getCodeBlocks(this);
            case 'zephyr':
                return zephyr.getCodeBlocks(this);
            case 'simulator':
                return simulator.getCodeBlocks(this);
            default:
                return tios.getCodeBlocks(this);
        }
    }

    getDetails(): string {
        return `##### Timer Start
This block starts a timer with the specified duration in seconds.`;
    }
}
